import { memo, useCallback } from "react"
import { IconEnvelopeCheckOutline18 as MailCheckIcon } from "nucleo-ui-outline-18"
import { z } from "zod"

import type { AllowedEmailDomain } from "@productlane/db/browser"
import { domainValidationRegex } from "@productlane/lib/constants"
import { commonEmailProviders } from "@productlane/lib/src/browser"
import {
	Dialog,
	Form,
	FormInput,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"

const FormSchema = z.object({
	domain: z
		.string({ required_error: "You must provide a domain" })
		.min(1, "You must provide a domain")
		.refine((value) => domainValidationRegex.test(value), "Invalid domain"),
	verificationEmail: z
		.string({ required_error: "You must provide a verification email" })
		.email("Invalid verification email"),
})

interface IProps {
	open: boolean
	onClose: (data?: Omit<AllowedEmailDomain, "hashedVerificationToken">) => void
}

export const AddEmailDomainModal = memo(({ open, onClose }: IProps) => {
	const ctx = api.useUtils()

	const createAllowedDomainMutation =
		api.workspace.createAllowedEmailDomain.useMutation({
			onSuccess: async () => {
				await ctx.workspace.getAllowedEmailDomains.invalidate()

				toast.success({
					description:
						"Successfully created allowed domain! Please check the verification email inbox to activate the domain.",
					title: "Domain verification started",
				})
			},
			onError: (error) => {
				toast.error({
					title: "Couldn't add email domain",
					description:
						error.message || "Failed to add domain. Please try again.",
				})
			},
		})

	const form = useZodForm({
		schema: FormSchema,
		mode: "onTouched",
		defaultValues: {},
	})

	const domainWatch = form.watch("domain")

	const handleSubmit = useCallback(
		async (values: z.infer<typeof FormSchema>) => {
			// we need to strip everything from the domain to make sure its in the correct format. the correct format is "example.com", no protocol or anything else
			values = {
				...values,
				domain: values.domain.replace(/(^\w+:|^)\/\//, "").split("/")[0]!,
			}

			if (commonEmailProviders.includes(values.domain)) {
				return toast.error({
					title: "Invalid domain",
					description: "The domain you provided is not allowed.",
				})
			}

			if (!values.verificationEmail.endsWith(values.domain)) {
				return toast.error({
					title: "Invalid email",
					description: "The email address must end with the domain.",
				})
			}

			const allowedDomain =
				await createAllowedDomainMutation.mutateAsync(values)

			onClose(allowedDomain)
		},
		[createAllowedDomainMutation, onClose],
	)

	return (
		<Dialog
			onOpenChange={(value) => {
				if (!value) {
					onClose()
				}
			}}
			isOpen={open}
			title={
				<div className="flex items-center">
					<MailCheckIcon className="mr-2 h-4 w-4 text-green-500" />
					Add allowed domain
				</div>
			}
			description="An email will be sent to verify that you have access to the domain."
		>
			<Form form={form} className="relative" onSubmit={handleSubmit}>
				<div className="relative mt-3 flex w-full flex-col space-y-4">
					<div className="space-y-3">
						<FormInput
							errorMessage={form.formState.errors.domain?.message}
							name="domain"
							label="Domain"
							placeholder="Domain"
							className="flex-1"
						/>
						<FormInput
							errorMessage={form.formState.errors.verificationEmail?.message}
							name="verificationEmail"
							label="Email for verification"
							autoComplete="email"
							placeholder={
								!domainWatch
									? "Email address for the domain"
									: `Email address ending in @${domainWatch}`
							}
							className="flex-1"
						/>
					</div>
					<div className="flex justify-end">
						<SubmitButton>Add domain</SubmitButton>
					</div>
				</div>
			</Form>
		</Dialog>
	)
})
